import React from 'react'
import styled from 'styled-components'
import { darken, lighten } from 'polished'

import { RowBetween } from '../Row'
import { ChevronDown } from 'react-feather'
import { Button as RebassButton, ButtonProps } from 'rebass/styled-components'

import { renderPendingContent } from './renderPendingContent'
import { useAsyncAction } from './useAsyncAction'

export { useAsyncAction } from './useAsyncAction'
export type { UseAsyncActionOptions, UseAsyncActionResult } from './useAsyncAction'

/**
 * Optional pending UI for action buttons (`ButtonPrimary`, `ButtonConfirmed`, `ButtonError`).
 *
 * - `pending`: external busy flag (OR with auto pending from async `onClick`)
 * - `pendingLabel`: shown with spinner while busy; defaults to `children`
 */
export type AsyncActionProps = {
  pending?: boolean
  pendingLabel?: React.ReactNode
}

const Base = styled(RebassButton)<{
  padding?: string
  width?: string
  borderRadius?: string
  altDisabledStyle?: boolean
}>`
  padding: ${({ padding }) => (padding ? padding : '18px')};
  width: ${({ width }) => (width ? width : '100%')};
  font-weight: 500;
  text-align: center;
  border-radius: 20px;
  border-radius: ${({ borderRadius }) => borderRadius && borderRadius};
  outline: none;
  border: 1px solid transparent;
  color: white;
  text-decoration: none;
  display: flex;
  justify-content: center;
  flex-wrap: nowrap;
  align-items: center;
  cursor: pointer;
  position: relative;
  z-index: 1;
  &:disabled {
    cursor: auto;
  }

  > * {
    user-select: none;
  }
`

/** Styled primary button — use for `styled(ButtonPrimaryStyle)` extensions. */
export const ButtonPrimaryStyle = styled(Base)`
  background-color: ${({ theme }) => theme.ctaBg};
  color: ${({ theme }) => theme.ctaText};
  box-shadow: 0 0 15px rgba(255, 234, 128, 0.22);
  &:focus {
    box-shadow: 0 0 0 1pt ${({ theme }) => darken(0.08, theme.ctaBg)}, 0 0 18px rgba(255, 234, 128, 0.28);
    background-color: ${({ theme }) => darken(0.05, theme.ctaBg)};
  }
  &:hover {
    background-color: ${({ theme }) => darken(0.05, theme.ctaBg)};
    box-shadow: 0 0 18px rgba(255, 234, 128, 0.3);
  }
  &:active {
    box-shadow: 0 0 0 1pt ${({ theme }) => darken(0.12, theme.ctaBg)}, 0 0 12px rgba(255, 234, 128, 0.22);
    background-color: ${({ theme }) => darken(0.1, theme.ctaBg)};
  }
  &:disabled {
    background-color: ${({ theme, altDisabledStyle }) => (altDisabledStyle ? theme.ctaBg : theme.bg3)};
    color: ${({ theme, altDisabledStyle }) => (altDisabledStyle ? theme.ctaText : theme.text3)};
    cursor: auto;
    box-shadow: none;
    border: 1px solid transparent;
    outline: none;
    opacity: ${({ altDisabledStyle }) => (altDisabledStyle ? '0.7' : '1')};
  }
`

type StyledButtonComponent = React.ComponentType<Record<string, unknown>>

function AsyncActionButton({
  StyledButton,
  onClick,
  disabled,
  pending,
  pendingLabel,
  children,
  altDisabledStyle,
  ...rest
}: AsyncActionProps & {
  StyledButton: StyledButtonComponent
  altDisabledStyle?: boolean
  disabled?: boolean
  children?: React.ReactNode
} & Record<string, unknown>) {
  const { isBusy, handleClick } = useAsyncAction({
    onClick: onClick as (() => void | Promise<void>) | undefined,
    disabled: Boolean(disabled),
    pending,
  })

  const showPendingUi = isBusy && !disabled

  return (
    <StyledButton
      {...rest}
      disabled={disabled || isBusy}
      altDisabledStyle={altDisabledStyle || isBusy}
      onClick={handleClick}
    >
      {showPendingUi ? renderPendingContent(pendingLabel, children) : children}
    </StyledButton>
  )
}

type StyledPrimaryProps = React.ComponentProps<typeof ButtonPrimaryStyle>

export type ButtonPrimaryProps = AsyncActionProps & StyledPrimaryProps
export function ButtonPrimary({
  onClick,
  disabled,
  pending,
  pendingLabel,
  children,
  altDisabledStyle,
  ...rest
}: ButtonPrimaryProps) {
  return (
    <AsyncActionButton
      StyledButton={ButtonPrimaryStyle}
      onClick={onClick}
      disabled={disabled}
      pending={pending}
      pendingLabel={pendingLabel}
      altDisabledStyle={altDisabledStyle}
      {...rest}
    >
      {children}
    </AsyncActionButton>
  )
}

export const ButtonLight = styled(Base)`
  background-color: ${({ theme }) => theme.primary5};
  color: ${({ theme }) => theme.primaryText1};
  font-size: 16px;
  font-weight: 500;
  &:focus {
    box-shadow: 0 0 0 1pt ${({ theme, disabled }) => !disabled && darken(0.03, theme.primary5)};
    background-color: ${({ theme, disabled }) => !disabled && darken(0.03, theme.primary5)};
  }
  &:hover {
    background-color: ${({ theme, disabled }) => !disabled && darken(0.03, theme.primary5)};
  }
  &:active {
    box-shadow: 0 0 0 1pt ${({ theme, disabled }) => !disabled && darken(0.05, theme.primary5)};
    background-color: ${({ theme, disabled }) => !disabled && darken(0.05, theme.primary5)};
  }
  :disabled {
    opacity: 0.4;
    :hover {
      cursor: auto;
      background-color: ${({ theme }) => theme.primary5};
      box-shadow: none;
      border: 1px solid transparent;
      outline: none;
    }
  }
`

export const ButtonGray = styled(Base)`
  background-color: ${({ theme }) => theme.bg3};
  color: ${({ theme }) => theme.text2};
  font-size: 16px;
  font-weight: 500;
  /* Hover/active respecto a bg3: en paleta clara default bg2 === bg3, y darken(0.05, bg2) casi no cambiaba. */
  &:focus {
    box-shadow: 0 0 0 1pt ${({ theme, disabled }) => !disabled && darken(0.18, theme.bg3)};
    background-color: ${({ theme, disabled }) => !disabled && darken(0.12, theme.bg3)};
  }
  &:hover {
    background-color: ${({ theme, disabled }) => !disabled && darken(0.12, theme.bg3)};
  }
  &:active {
    box-shadow: 0 0 0 1pt ${({ theme, disabled }) => !disabled && darken(0.22, theme.bg3)};
    background-color: ${({ theme, disabled }) => !disabled && darken(0.2, theme.bg3)};
  }
`

export const ButtonSecondary = styled(Base)`
  background-color: ${({ theme }) => theme.primary5};
  color: ${({ theme }) => theme.primaryText1};
  font-size: 16px;
  border-radius: 8px;
  padding: ${({ padding }) => (padding ? padding : '10px')};

  &:focus {
    box-shadow: 0 0 0 1pt ${({ theme }) => theme.primary4};
    background-color: ${({ theme }) => theme.primary4};
  }
  &:hover {
    background-color: ${({ theme }) => theme.primary4};
  }
  &:active {
    box-shadow: 0 0 0 1pt ${({ theme }) => theme.primary4};
    background-color: ${({ theme }) => theme.primary4};
  }
  &:disabled {
    background-color: ${({ theme }) => theme.primary5};
    opacity: 50%;
    cursor: auto;
  }
`

export const ButtonPink = styled(Base)`
  background-color: ${({ theme }) => theme.ctaBg};
  color: ${({ theme }) => theme.ctaText};

  &:focus {
    box-shadow: 0 0 0 1pt ${({ theme }) => darken(0.05, theme.ctaBg)};
    background-color: ${({ theme }) => darken(0.05, theme.ctaBg)};
  }
  &:hover {
    background-color: ${({ theme }) => darken(0.05, theme.ctaBg)};
  }
  &:active {
    box-shadow: 0 0 0 1pt ${({ theme }) => darken(0.1, theme.ctaBg)};
    background-color: ${({ theme }) => darken(0.1, theme.ctaBg)};
  }
  &:disabled {
    background-color: ${({ theme }) => theme.ctaBg};
    opacity: 50%;
    cursor: auto;
  }
`

export const ButtonOutlined = styled(Base)`
  border: 1px solid ${({ theme }) => theme.bg2};
  background-color: transparent;
  color: ${({ theme }) => theme.text1};

  &:focus {
    box-shadow: 0 0 0 1px ${({ theme }) => theme.bg4};
  }
  &:hover {
    box-shadow: 0 0 0 1px ${({ theme }) => theme.bg4};
  }
  &:active {
    box-shadow: 0 0 0 1px ${({ theme }) => theme.bg4};
  }
  &:disabled {
    opacity: 50%;
    cursor: auto;
  }
`

export const ButtonEmpty = styled(Base)`
  background-color: transparent;
  color: ${({ theme }) => theme.primary1};
  display: flex;
  justify-content: center;
  align-items: center;

  &:focus {
    background-color: ${({ theme }) => theme.advancedBG};
  }
  &:hover {
    background-color: ${({ theme }) => theme.advancedBG};
  }
  &:active {
    background-color: ${({ theme }) => theme.advancedBG};
  }
  &:disabled {
    opacity: 50%;
    cursor: auto;
  }
`

export const ButtonWhite = styled(Base)`
  border: 1px solid #edeef2;
  background-color: ${({ theme }) => theme.bg1};
  color: black;

  &:focus {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    box-shadow: 0 0 0 1pt ${darken(0.05, '#edeef2')};
  }
  &:hover {
    box-shadow: 0 0 0 1pt ${darken(0.1, '#edeef2')};
  }
  &:active {
    box-shadow: 0 0 0 1pt ${darken(0.1, '#edeef2')};
  }
  &:disabled {
    opacity: 50%;
    cursor: auto;
  }
`

const ButtonConfirmedStyle = styled(Base)`
  background-color: ${({ theme }) => lighten(0.5, theme.green1)};
  color: ${({ theme }) => theme.green1};
  border: 1px solid ${({ theme }) => theme.green1};

  &:disabled {
    opacity: 50%;
    cursor: auto;
  }
`

const ButtonErrorStyle = styled(Base)`
  background-color: ${({ theme }) => theme.red1};
  border: 1px solid ${({ theme }) => theme.red1};

  &:focus {
    box-shadow: 0 0 0 1pt ${({ theme }) => darken(0.05, theme.red1)};
    background-color: ${({ theme }) => darken(0.05, theme.red1)};
  }
  &:hover {
    background-color: ${({ theme }) => darken(0.05, theme.red1)};
  }
  &:active {
    box-shadow: 0 0 0 1pt ${({ theme }) => darken(0.1, theme.red1)};
    background-color: ${({ theme }) => darken(0.1, theme.red1)};
  }
  &:disabled {
    opacity: 50%;
    cursor: auto;
    box-shadow: none;
    background-color: ${({ theme }) => theme.red1};
    border: 1px solid ${({ theme }) => theme.red1};
  }
`

export function ButtonConfirmed({
  confirmed,
  altDisabledStyle,
  onClick,
  disabled,
  pending,
  pendingLabel,
  children,
  ...rest
}: { confirmed?: boolean; altDisabledStyle?: boolean } & ButtonPrimaryProps) {
  if (confirmed) {
    return <ButtonConfirmedStyle {...rest} disabled={disabled} onClick={onClick}>{children}</ButtonConfirmedStyle>
  }

  return (
    <AsyncActionButton
      StyledButton={ButtonPrimaryStyle}
      onClick={onClick}
      disabled={disabled}
      pending={pending}
      pendingLabel={pendingLabel}
      altDisabledStyle={altDisabledStyle}
      {...rest}
    >
      {children}
    </AsyncActionButton>
  )
}

export function ButtonError({
  error,
  onClick,
  disabled,
  pending,
  pendingLabel,
  children,
  ...rest
}: { error?: boolean } & ButtonPrimaryProps) {
  const StyledButton = error ? ButtonErrorStyle : ButtonPrimaryStyle

  return (
    <AsyncActionButton
      StyledButton={StyledButton}
      onClick={onClick}
      disabled={disabled}
      pending={pending}
      pendingLabel={pendingLabel}
      {...rest}
    >
      {children}
    </AsyncActionButton>
  )
}

export function ButtonDropdown({ disabled = false, children, ...rest }: ButtonPrimaryProps) {
  return (
    <ButtonPrimary {...rest} disabled={disabled}>
      <RowBetween>
        <div style={{ display: 'flex', alignItems: 'center' }}>{children}</div>
        <ChevronDown size={24} />
      </RowBetween>
    </ButtonPrimary>
  )
}

export function ButtonDropdownLight({ disabled = false, children, ...rest }: { disabled?: boolean } & ButtonProps) {
  return (
    <ButtonOutlined {...rest} disabled={disabled}>
      <RowBetween>
        <div style={{ display: 'flex', alignItems: 'center' }}>{children}</div>
        <ChevronDown size={24} />
      </RowBetween>
    </ButtonOutlined>
  )
}

export function ButtonRadio({ active, ...rest }: { active?: boolean } & ButtonPrimaryProps) {
  if (!active) {
    return <ButtonWhite {...rest} />
  } else {
    return <ButtonPrimary {...rest} />
  }
}
